import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaGraduationCap } from "react-icons/fa";
import "./Qualifications.css";
import JobSeekerNavbar from "../../../../Pages/Navbar/JobSeekerNavbar";
import Swal from "sweetalert2";
import BaseUrl from '../../../../ApiServices/BaseUrl';
import { Container, Card, Row, Col, Button, Dropdown, Form } from 'react-bootstrap';

function Qualifications() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = location.state || {};

  // Log userId to console
  useEffect(() => {
    console.log('Received userId:', userId);
  }, [userId]);

  // Arrays for button groups
  const undergraduate = ["MBBS", "MBCHB", "MD", "DO"];
  const postgraduate = ["MS", "MCH", "DM", "DNB"];
  const specialty = ["Cardiology", "Neurology", "Oncology", "Orthopedics", "Pediatrics"];
  const superSpecialty = ["Cardiothoracic Surgery", "Neurosurgery", "Pediatric Surgery", "Plastic Surgery", "Urology"];
  const fellowship1 = ["FRCP", "FRCS", "FRCR", "FRCPath"];
  const fellowship2 = ["FRCOG", "FRACS", "FACP", "FRCS (C)"];
  const fellowship3 = ["FACC", "FAGG", "FASN", "FASCO"];
  const fellowship4 = ["FEB", "FIC", "FRACP", "MRCP"];
  const english1 = ["TOEFL", "IELTS", "OET", "PTE"];
  const english2 = ["CPE", "CAE", "FCE", "PET"];
  const english3 = ["KET", "BEC", "BULATS"];
  const otherLanguages = [
    "DELF", "DALF", "TCF", "DELE", "TESTDAF", "GOETHE-ZERTIFIKAT", "DSD", "CILS", "PLIDA",
    "HSK", "BTC", "JLPT", "NAT-TEST", "ALPT", "ACTFL", "TORFL", "TRKI", "CELPE-Bras", "DEPLE", "TOPIK", "KLAT"
  ];
  const cultural = [
    "Cultural Competence in Healthcare", "Cross-Cultural Communication", "Diversity and Inclusion in Healthcare",
    "IMG Program", "CCC", "CMI", "Cultural Awareness Training", "Cross-Cultural Training", "Global Health Training", "Other"
  ];
  const certificateOptions = ["Yes", "No", "Applied"];
  const countries = ["India", "USA", "UK", "Australia", "Canada"];
  const languageLevels = [
    "Basic language skill", "Elementary language skills", "Intermediate language skills",
    "Upper-intermediate language skills", "Advanced language skills", "Proficient language skills",
    "Limited working proficiency", "Professional working proficiency",
    "Advanced professional proficiency", "Functionally native proficiency", "No proficiency"
  ];

  // State for form fields
  const [selectedUG, setSelectedUG] = useState("");
  const [selectedPG, setSelectedPG] = useState("");
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [selectedSuperSpecialties, setSelectedSuperSpecialties] = useState([]);
  const [selectedFellowships, setSelectedFellowships] = useState([]);
  const [otherQualifications, setOtherQualifications] = useState("");
  const [selectedEnglish, setSelectedEnglish] = useState("");
  const [selectedOtherLanguages, setSelectedOtherLanguages] = useState([]);
  const [languageProficiency, setLanguageProficiency] = useState("");
  const [selectedCultural, setSelectedCultural] = useState([]);
  const [prometricCertificate, setPrometricCertificate] = useState("");
  const [prometricIssuedCountry, setPrometricIssuedCountry] = useState("");
  const [dataflowCertificate, setDataflowCertificate] = useState("");
  const [dataflowIssuedCountry, setDataflowIssuedCountry] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Helper function to filter options for dropdown
  const filterOptions = (options, searchTerm) => {
    if (!searchTerm) return options;
    return options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Custom Dropdown Component (same as in JobPreference)
  const CustomDropdown = ({
    title,
    field,
    options,
    selectedValue,
    onSelect,
    required = false,
    menuClassName = '',
    disabled = false
  }) => {
    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filteredOptions = filterOptions(options, localSearchTerm);

    return (
      <div>
        <Dropdown
          className="mb-3"
          show={isOpen}
          onToggle={(isOpen) => setIsOpen(isOpen)}
          disabled={disabled}
        >
          <Dropdown.Toggle
            variant="light"
            className="language-select w-100 d-flex justify-content-between align-items-center"
            id={`dropdown-${field}`}
            disabled={disabled}
          >
            {selectedValue || `${title}${required ? ' *' : ''}`}
          </Dropdown.Toggle>
          <Dropdown.Menu className={`w-100 ${menuClassName}`}>
            <div className="px-2 py-1">
              <Form.Control
                type="text"
                placeholder={`Search ${title.toLowerCase()}...`}
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="mb-2"
                autoFocus
              />
            </div>
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <Dropdown.Item
                    key={option}
                    active={selectedValue === option}
                    onClick={() => {
                      onSelect(field, option);
                      setLocalSearchTerm('');
                      setIsOpen(false);
                    }}
                  >
                    {option}
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item disabled>No matches found</Dropdown.Item>
              )}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  // Fetch existing data
  useEffect(() => {
    const fetchQualificationsData = async () => {
      if (!userId) return;

      try {
        setIsLoading(true);
        const response = await fetch(`${BaseUrl}/api/job-seeker/doctor/${userId}`);

        if (response.ok) {
          const data = await response.json();
          console.log('Fetched qualifications data:', data);

          // Set qualification fields
          if (data.dtr_ug_qualification && data.dtr_ug_qualification !== "null") {
            setSelectedUG(data.dtr_ug_qualification);
          }

          if (data.dtr_pg_qualification && data.dtr_pg_qualification !== "null") {
            setSelectedPG(data.dtr_pg_qualification);
          }

          if (data.dtr_specialty_qualifications && data.dtr_specialty_qualifications !== "null") {
            try {
              const parsedSpecialties = JSON.parse(data.dtr_specialty_qualifications);
              if (Array.isArray(parsedSpecialties)) {
                setSelectedSpecialties(parsedSpecialties);
              }
            } catch (e) {
              console.error('Error parsing specialties:', e);
            }
          }

          if (data.dtr_super_spl_qualifications && data.dtr_super_spl_qualifications !== "null") {
            try {
              const parsedSuperSpecialties = JSON.parse(data.dtr_super_spl_qualifications);
              if (Array.isArray(parsedSuperSpecialties)) {
                setSelectedSuperSpecialties(parsedSuperSpecialties);
              }
            } catch (e) {
              console.error('Error parsing super specialties:', e);
            }
          }

          if (data.dtr_fellowship_qualifications && data.dtr_fellowship_qualifications !== "null") {
            try {
              const parsedFellowships = JSON.parse(data.dtr_fellowship_qualifications);
              if (Array.isArray(parsedFellowships)) {
                setSelectedFellowships(parsedFellowships);
              }
            } catch (e) {
              setSelectedFellowships([data.dtr_fellowship_qualifications]);
            }
          }

          if (data.dtr_other_qualifications && data.dtr_other_qualifications !== "null") {
            setOtherQualifications(data.dtr_other_qualifications);
          }

          if (data.dtr_english_lang_qualifications && data.dtr_english_lang_qualifications !== "null") {
            setSelectedEnglish(data.dtr_english_lang_qualifications);
          }

          if (data.dtr_other_language_qualifications && data.dtr_other_language_qualifications !== "null") {
            try {
              const parsedOtherLanguages = JSON.parse(data.dtr_other_language_qualifications);
              if (Array.isArray(parsedOtherLanguages)) {
                setSelectedOtherLanguages(parsedOtherLanguages);
              }
            } catch (e) {
              setSelectedOtherLanguages([data.dtr_other_language_qualifications]);
            }
          }

          if (data.dtr_language_proficiency && data.dtr_language_proficiency !== "null") {
            setLanguageProficiency(data.dtr_language_proficiency);
          }

          if (data.dtr_cultural_qualifications && data.dtr_cultural_qualifications !== "null") {
            try {
              const parsedCultural = JSON.parse(data.dtr_cultural_qualifications);
              if (Array.isArray(parsedCultural)) {
                setSelectedCultural(parsedCultural);
              }
            } catch (e) {
              setSelectedCultural([data.dtr_cultural_qualifications]);
            }
          }

          if (data.dtr_prometric_certificate !== null && data.dtr_prometric_certificate !== undefined) {
            if (data.dtr_prometric_certificate === true || data.dtr_prometric_certificate === 1) {
              setPrometricCertificate("Yes");
            } else if (data.dtr_prometric_certificate === false || data.dtr_prometric_certificate === 0) {
              setPrometricCertificate("No");
            }
          }

          if (data.dtr_prometric_issued_country && data.dtr_prometric_issued_country !== "null") {
            setPrometricIssuedCountry(data.dtr_prometric_issued_country);
          }

          if (data.dtr_data_flow_certificate !== null && data.dtr_data_flow_certificate !== undefined) {
            if (data.dtr_data_flow_certificate === true || data.dtr_data_flow_certificate === 1) {
              setDataflowCertificate("Yes");
            } else if (data.dtr_data_flow_certificate === false || data.dtr_data_flow_certificate === 0) {
              setDataflowCertificate("No");
            }
          }

          if (data.dtr_data_flow_issued_by_country && data.dtr_data_flow_issued_by_country !== "null") {
            setDataflowIssuedCountry(data.dtr_data_flow_issued_by_country);
          }
        } else if (response.status !== 404) {
          throw new Error('Failed to fetch qualifications data');
        }
      } catch (err) {
        console.error('Error fetching qualifications data:', err);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to load your qualifications information. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#d33',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      fetchQualificationsData();
    }
  }, [userId]);

  // Show success alert using SweetAlert2
  const showSuccessAlert = () => {
    Swal.fire({
      title: 'Success!',
      text: 'Qualifications information saved successfully!',
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: '#3085d6',
    });
  };

  // Handle save data to API
  const handleSave = async () => {
    if (!userId) {
      Swal.fire({
        title: 'Error!',
        text: 'User ID is missing. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
      return false;
    }

    setIsSaving(true);

    try {
      const formData = {
        user_id: userId,
        dtr_user_id: userId,
        dtr_ug_qualification: selectedUG,
        dtr_pg_qualification: selectedPG,
        dtr_specialty_qualifications: selectedSpecialties,
        dtr_super_spl_qualifications: selectedSuperSpecialties,
        dtr_fellowship_qualifications: selectedFellowships,
        dtr_other_qualifications: otherQualifications,
        dtr_english_lang_qualifications: selectedEnglish,
        dtr_other_language_qualifications: selectedOtherLanguages,
        dtr_language_proficiency: languageProficiency,
        dtr_cultural_qualifications: selectedCultural,
        dtr_prometric_certificate: prometricCertificate === "Yes",
        dtr_prometric_issued_country: prometricCertificate === "Yes" ? prometricIssuedCountry : "",
        dtr_data_flow_certificate: dataflowCertificate === "Yes",
        dtr_data_flow_issued_by_country: dataflowCertificate === "Yes" ? dataflowIssuedCountry : ""
      };

      console.log('Saving qualifications data:', formData);

      const response = await fetch(`${BaseUrl}/api/job-seeker/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('API Response:', result);
        showSuccessAlert();
        return true;
      } else {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        throw new Error(errorData.message || 'Failed to save qualifications information');
      }
    } catch (error) {
      console.error('Error saving qualifications information:', error);
      Swal.fire({
        title: 'Error!',
        text: error.message || 'Error saving qualifications information. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle next step
  const handleNext = async () => {
    const success = await handleSave();
    if (success) {
      navigate('/resume', {
        state: { userId: userId }
      });
    }
  };

  // Handle back step
  const handleBack = () => {
    navigate('/experience', {
      state: { userId: userId }
    });
  };

  // Handle navigation button clicks
  const handleNavigation = (buttonName) => {
    if (buttonName === "Next") {
      handleNext();
    } else if (buttonName === "Back") {
      handleBack();
    } else if (buttonName === "Save") {
      handleSave();
    }
  };

  // Toggle selection for multi-select fields
  const toggleSelection = (item, field, setter) => {
    if (field.includes(item)) {
      setter(field.filter(i => i !== item));
    } else {
      setter([...field, item]);
    }
  };

  // Remove selected item
  const removeSelectedItem = (item, selectedItems, setSelectedItems) => {
    setSelectedItems(selectedItems.filter(i => i !== item));
  };

  // Helper to render selected items as badges
  const renderSelectedBadges = (selectedItems, setSelectedItems, title) => {
    if (selectedItems.length === 0) return null;

    return (
      <div className="mb-3">
        <div className="selected-locations-container p-3 bg-light rounded">
          <h6 className="mb-2 fw-semibold">{title}:</h6>
          <div className="d-flex flex-wrap gap-2">
            {selectedItems.map((item, index) => (
              <div key={index} className="selected-location-badge d-flex align-items-center bg-white border rounded-pill px-3 py-1">
                <span className="me-2">{item}</span>
                <button
                  type="button"
                  className="btn-close btn-close-sm"
                  aria-label="Close"
                  onClick={() => removeSelectedItem(item, selectedItems, setSelectedItems)}
                  style={{ fontSize: '0.5rem' }}
                ></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <>
        <JobSeekerNavbar />
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <JobSeekerNavbar />
      <div className="qualifications-wrapper" style={{ marginTop: "84px", width: '96%', marginLeft: '2%' }}>
        <Container className="mt-4 px-4 pb-5">
          <Card className="shadow border-0 professional-info-card">
            <Card.Header className="professional-info-header">
              <h2 className="m-0 text-white text-center">
                <FaGraduationCap className="me-2" />
                Qualifications
              </h2>
            </Card.Header>
            <Card.Body className="professional-info-body">

              {/* Undergraduate Qualifications */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Undergraduate Qualifications
                </h4>
                <Row className="g-3 mb-3">
                  {undergraduate.map((btn, idx) => (
                    <Col xs={6} lg={3} key={idx}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${selectedUG === btn ? 'selected-button' : ''}`}
                        onClick={() => setSelectedUG(btn)}
                        style={{ backgroundColor: selectedUG === btn ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Postgraduate Qualifications */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Postgraduate Qualifications
                </h4>
                <Row className="g-3 mb-3">
                  {postgraduate.map((btn, idx) => (
                    <Col xs={6} lg={3} key={idx}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${selectedPG === btn ? 'selected-button' : ''}`}
                        onClick={() => setSelectedPG(btn)}
                        style={{ backgroundColor: selectedPG === btn ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Specialty-Specific Qualifications */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Specialty-Specific Qualifications
                </h4>
                {renderSelectedBadges(selectedSpecialties, setSelectedSpecialties, "Selected Specialties")}
                <Row className="g-3 mb-3">
                  {specialty.map((btn, idx) => (
                    <Col xs={6} lg={3} key={idx}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${selectedSpecialties.includes(btn) ? 'selected-button' : ''}`}
                        onClick={() => toggleSelection(btn, selectedSpecialties, setSelectedSpecialties)}
                        style={{ backgroundColor: selectedSpecialties.includes(btn) ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Super-Specialty Qualifications */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Super-Specialty Qualifications
                </h4>
                {renderSelectedBadges(selectedSuperSpecialties, setSelectedSuperSpecialties, "Selected Super Specialties")}
                <Row className="g-3 mb-3">
                  {superSpecialty.map((btn, idx) => (
                    <Col xs={6} lg={3} key={idx}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${selectedSuperSpecialties.includes(btn) ? 'selected-button' : ''}`}
                        onClick={() => toggleSelection(btn, selectedSuperSpecialties, setSelectedSuperSpecialties)}
                        style={{ backgroundColor: selectedSuperSpecialties.includes(btn) ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Fellowship Qualifications */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Fellowship Qualifications
                </h4>
                {renderSelectedBadges(selectedFellowships, setSelectedFellowships, "Selected Fellowships")}
                
                {/* Fellowship Group 1 */}
                <Row className="g-3 mb-3">
                  {fellowship1.map((btn, idx) => (
                    <Col xs={6} lg={3} key={idx}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${selectedFellowships.includes(btn) ? 'selected-button' : ''}`}
                        onClick={() => toggleSelection(btn, selectedFellowships, setSelectedFellowships)}
                        style={{ backgroundColor: selectedFellowships.includes(btn) ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>

                {/* Fellowship Group 2 */}
                <Row className="g-3 mb-3">
                  {fellowship2.map((btn, idx) => (
                    <Col xs={6} lg={3} key={idx}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${selectedFellowships.includes(btn) ? 'selected-button' : ''}`}
                        onClick={() => toggleSelection(btn, selectedFellowships, setSelectedFellowships)}
                        style={{ backgroundColor: selectedFellowships.includes(btn) ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>

                {/* Fellowship Group 3 */}
                <Row className="g-3 mb-3">
                  {fellowship3.map((btn, idx) => (
                    <Col xs={6} lg={3} key={idx}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${selectedFellowships.includes(btn) ? 'selected-button' : ''}`}
                        onClick={() => toggleSelection(btn, selectedFellowships, setSelectedFellowships)}
                        style={{ backgroundColor: selectedFellowships.includes(btn) ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>

                {/* Fellowship Group 4 */}
                <Row className="g-3 mb-3">
                  {fellowship4.map((btn, idx) => (
                    <Col xs={6} lg={3} key={idx}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${selectedFellowships.includes(btn) ? 'selected-button' : ''}`}
                        onClick={() => toggleSelection(btn, selectedFellowships, setSelectedFellowships)}
                        style={{ backgroundColor: selectedFellowships.includes(btn) ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Other Qualifications Input */}
              {/* <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Other Qualifications
                </h4>
                <input
                  type="text"
                  className="qualifications-input form-control"
                  placeholder="Please type the following qualifications here, if any."
                  value={otherQualifications}
                  onChange={(e) => setOtherQualifications(e.target.value)}
                  maxLength={20}
                />
                <p className="qualifications-note text-center mt-2">
                  Maximum typing character is 20 characters
                </p>
              </div> */}

              {/* English Language Qualifications */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  English Language Qualifications
                </h4>
                <Row className="g-3 mb-3">
                  {english1.map((btn, idx) => (
                    <Col xs={6} lg={3} key={idx}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${selectedEnglish === btn ? 'selected-button' : ''}`}
                        onClick={() => setSelectedEnglish(btn)}
                        style={{ backgroundColor: selectedEnglish === btn ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>

                <Row className="g-3 mb-3">
                  {english2.map((btn, idx) => (
                    <Col xs={6} lg={3} key={idx}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${selectedEnglish === btn ? 'selected-button' : ''}`}
                        onClick={() => setSelectedEnglish(btn)}
                        style={{ backgroundColor: selectedEnglish === btn ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>

                <Row className="g-3 mb-3">
                  {english3.map((btn, idx) => (
                    <Col xs={6} lg={3} key={idx}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${selectedEnglish === btn ? 'selected-button' : ''}`}
                        onClick={() => setSelectedEnglish(btn)}
                        style={{ backgroundColor: selectedEnglish === btn ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Other Language Qualifications */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Other Language Qualifications
                </h4>
                {renderSelectedBadges(selectedOtherLanguages, setSelectedOtherLanguages, "Selected Other Languages")}
                
                {/* First 4 items */}
                <Row className="g-3 mb-3">
                  {otherLanguages.slice(0, 4).map((btn, idx) => (
                    <Col xs={6} lg={3} key={idx}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${selectedOtherLanguages.includes(btn) ? 'selected-button' : ''}`}
                        onClick={() => toggleSelection(btn, selectedOtherLanguages, setSelectedOtherLanguages)}
                        style={{ backgroundColor: selectedOtherLanguages.includes(btn) ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>

                {/* Next 4 items */}
                <Row className="g-3 mb-3">
                  {otherLanguages.slice(4, 8).map((btn, idx) => (
                    <Col xs={6} lg={3} key={idx}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${selectedOtherLanguages.includes(btn) ? 'selected-button' : ''}`}
                        onClick={() => toggleSelection(btn, selectedOtherLanguages, setSelectedOtherLanguages)}
                        style={{ backgroundColor: selectedOtherLanguages.includes(btn) ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>

                {/* Custom Dropdown for remaining items */}
                {otherLanguages.length > 8 && (
                  <div className="mb-3">
                    <CustomDropdown
                      title="Select other language qualification"
                      field="other_language_dropdown"
                      options={otherLanguages.slice(8)}
                      selectedValue={selectedOtherLanguages.find(lang => 
                        otherLanguages.slice(8).includes(lang)
                      ) || ""}
                      onSelect={(field, lang) => {
                        toggleSelection(lang, selectedOtherLanguages, setSelectedOtherLanguages);
                      }}
                      required={false}
                    />
                  </div>
                )}
              </div>

              {/* Language Proficiency Levels */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Language Proficiency Levels
                </h4>
                <CustomDropdown
                  title="Select Language Proficiency Level"
                  field="language_proficiency"
                  options={languageLevels}
                  selectedValue={languageProficiency}
                  onSelect={(field, value) => setLanguageProficiency(value)}
                  required={false}
                />
              </div>

              {/* Prometric Certificate Section */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Do you have Prometric certificate?
                </h4>
                <Row className="g-3 mb-4">
                  {certificateOptions.map((btn, i) => (
                    <Col xs={4} lg={4} key={i}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${prometricCertificate === btn ? 'selected-button' : ''}`}
                        onClick={() => setPrometricCertificate(btn)}
                        style={{ backgroundColor: prometricCertificate === btn ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>

                {prometricCertificate === "Yes" && (
                  <>
                    <CustomDropdown
                      title="Issued by which Country (Select)"
                      field="prometric_country"
                      options={countries}
                      selectedValue={prometricIssuedCountry}
                      onSelect={(field, value) => setPrometricIssuedCountry(value)}
                      required={false}
                    />
                    {/* <p className="qualifications-note text-center mt-2">
                      A multiple option should be given for selecting the country.
                    </p> */}
                  </>
                )}
              </div>

              {/* Dataflow Certificate Section */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Do you have Data flow certificate?
                </h4>
                <Row className="g-3 mb-4">
                  {certificateOptions.map((btn, i) => (
                    <Col xs={4} lg={4} key={i}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${dataflowCertificate === btn ? 'selected-button' : ''}`}
                        onClick={() => setDataflowCertificate(btn)}
                        style={{ backgroundColor: dataflowCertificate === btn ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>

                {dataflowCertificate === "Yes" && (
                  <>
                    <CustomDropdown
                      title="Issued by which Country (Select)"
                      field="dataflow_country"
                      options={countries}
                      selectedValue={dataflowIssuedCountry}
                      onSelect={(field, value) => setDataflowIssuedCountry(value)}
                      required={false}
                    />
                    {/* <p className="qualifications-note text-center mt-2">
                      A multiple option should be given for selecting the country.
                    </p> */}
                  </>
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="additional-section mb-4" style={{ marginTop: '20px' }}>
                <div className="d-flex justify-content-between gap-2" style={{ width: "100%" }}>
                  {["Back", "Save", "Next"].map((btn, i) => (
                    <button
                      key={i}
                      className="nav-btn"
                      style={{ flex: 1 }}
                      onClick={() => handleNavigation(btn)}
                      disabled={isSaving}
                    >
                      {isSaving && (btn === "Save" || btn === "Next") ? 'Saving...' : btn}
                    </button>
                  ))}
                </div>
              </div>

            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
}

export default Qualifications;